import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

import { bindActionCreators } from 'redux';

import { parseQuery } from '@common/typescript/utils/url';
import { replaceSearchUrl } from '@common/react/components/Utils/Utils';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import * as Login from '@common/react/store/Login';
import { BaseApplicationState } from '@common/react/store';
import { TypeKeys as ItemKeys } from '@common/react/store/Item';
import * as ItemsState from '@common/react/store/ItemList';
import ChangePhoneModal from '@common/react/components/UI/ChangePhoneModal/ChangePhoneModal';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { BaseInvite } from '@commonTuna/react/objects/BaseInvite';
import { LogoLoaderPage } from '@commonTuna/react/components/UI/LogoLoader/LogoLoader';
import { PortalRegistrationStep } from '@commonTuna/react/objects/PortalRegistrationStep';

import '@app/scss/pages/register.scss';
import { PatientMainForm, UserRegistrationSteps } from '@app/components/Pages/Register/PatientMainForm';
import { PatientPhoto } from '@app/components/Pages/Register/PatientPhoto';
import InsuranceStep from '@app/components/Pages/Register/InsuranceStep';
import DriverLicenseStep from '@app/components/Pages/Register/DriverLicenseStep';
import { User } from '@app/objects/User';
import { SignHipaa } from '@app/components/Pages/Register/SignHipaa';
import { RegistrationFillForms } from '@app/components/Pages/Register/RegistrationFillForms';
import { ApplicationState } from '@app/store';
import Congratulations from '@app/components/Pages/Register/Congratulations';
import { BaseItemsPage, TabsPage } from '@app/objects/Page';
import { Insurance } from '@app/objects/Insurance';
import { DrivingLicense } from '@app/objects/DrivingLicense';

export type InsuranceStepPage = BaseItemsPage<Insurance>

export type DrivingLicenseStepPage = BaseItemsPage<DrivingLicense>

interface RegistrationPageType extends TabsPage<InsuranceStepPage | DrivingLicenseStepPage> {
	redirectUrl: string;
}

export const stepNames = [
	{ name: 'info', value: PortalRegistrationStep.Info },
	{ name: 'congratulations', value: PortalRegistrationStep.Congratulations },
	{ name: 'hipaa', value: PortalRegistrationStep.Hipaa },
	{ name: 'photo', value: PortalRegistrationStep.Photo },
	{ name: 'insurance', value: PortalRegistrationStep.Insurance },
	{ name: 'license', value: PortalRegistrationStep.License },
	{ name: 'forms', value: PortalRegistrationStep.Forms },
];

export interface PortalSettings {
	askForAvatar: boolean;
	askForInsurance: boolean;
	askForDrivingLicense: boolean;
}

interface ReduxProps {
	user: User | null;
	companyTemplateInvites: Array<BaseInvite>;
	userRegistrationSteps: UserRegistrationSteps;
}

type LoginActions = Login.LoginActionCreators<User, BaseApplicationState<User>>;

const PatientRegistration: React.FC<ReduxProps> = (props) => {
	const history = useHistory();
	const location = useLocation();

	const [currentStep, setCurrentStep] = React.useState<PortalRegistrationStep>(() => {
		const query = parseQuery(location.search);
		const stepFromUrl = stepNames.find((s) => s.name === query.step);
		return stepFromUrl ? stepFromUrl.value : PortalRegistrationStep.Info;
	});
	const { request } = useApplicationContext();
	const [user, setUser] = React.useState<User | undefined>(props.user ? props.user : undefined);
	const [steps, setSteps] = React.useState<Array<PortalRegistrationStep>>(props.userRegistrationSteps?.steps || []);
	const [hippa, setHippa] = React.useState<Array<BaseInvite>>(props.companyTemplateInvites || []);
	const [forms, setForms] = React.useState<Array<BaseInvite>>(props.userRegistrationSteps?.forms || []);
	const [stepAfterModal, setStepAfterModal] = React.useState<{ callback:() => void, phone: string } | null>(null);

	const dispatch = useDispatch();
	const loginActions: LoginActions = bindActionCreators(Login.getActionCreators(), dispatch);
	const pagesActions = bindActionCreators(ItemsState.getActionCreators(), dispatch);

	const completeRegistration = (needRedirect: boolean, redirectUrl?: string) => {
		request<User>('userCompletedRegistration', {
			id: user?.id || 0,
			completedRegistration: true,
		}).then((res) => {
			loginActions.updateUser({ completedRegistration: res.completedRegistration });
			needRedirect ? redirect(redirectUrl) : user && setUser({
				...user,
				completedRegistration: res.completedRegistration,
			});
		});
	};

	const setNextStepInner = (nextStep: PortalRegistrationStep | undefined, redirectUrl?: string) => {
		if (nextStep) {
			if (nextStep === PortalRegistrationStep.Forms) {
				completeRegistration(false);
			}

			setCurrentStep(nextStep);

			const step = stepNames.find((s) => s.value === nextStep);

			history.replace({
				...location,
				search: replaceSearchUrl(location.search, 'step', step.name),
			});
		} else if (user?.completedRegistration) {
			redirect(redirectUrl);
		} else {
			completeRegistration(true);
		}
	};

	const redirect = (redirectUrl?: string) => {
		if (redirectUrl) {
			history.push(`/${redirectUrl}`);
		} else {
			history.push('/dashboard');
		}
	};

	const onRegistrationSubmit = (response: UserRegistrationSteps, redirectUrl?: string) => {
		const userLogin = () => {
			dispatch({
				type: ItemKeys.INITSTORAGE,
				storageName: 'userRegistrationSteps',
				item: response,
			});
			setSteps(response.steps);
			setUser(response.user);
			setHippa(response.hippa);
			setForms(response.forms);
			loginActions.updateUser(response.user);

			const nextStep = response.steps[0];

			setNextStepInner(nextStep, redirectUrl);
		};

		const email = response.user.email;
		const phone = response.user.phoneNumber;

		if (!email && phone) {
			setStepAfterModal({ phone, callback: userLogin });
		} else {
			userLogin();
		}
	};

	const setNextStep = (updatedUser?: User, redirectUrl?: string) => {
		updatedUser && setUser({ ...user, ...updatedUser });

		const index = steps.indexOf(currentStep);

		const nextStep = steps[index + 1];

		setNextStepInner(nextStep, redirectUrl);
	};

	const refreshCompanyTemplateInvites = () => {
		pagesActions.refreshPages('companyTemplateInvites', 'inviteList', {
			isCompanyTemplate: true,
			isAnswered: false,
		});
	};

	const renderButtons = (handleSubmit: () => void) => {
		return <div className="registration-buttons">
			<button type="button" className="btn btn-primary" onClick={handleSubmit}>Next</button>
			{/* <button type="button" className="btn btn-next btn-default" onClick={setNextStep}>Next</button> */}
		</div>;
	};

	const getCurrentStep = (serverPage: RegistrationPageType) => {
		const innerPage = serverPage.innerPage;

		if (stepAfterModal !== null && currentStep === PortalRegistrationStep.Info) {
			return <ChangePhoneModal
				defaultConfirmVisible
				finalAction={() => {
					stepAfterModal.callback();
				}}
				savedPhoneNumber={stepAfterModal.phone}
				className="change-info-modal"
				modalOptions={{
					closable: false,
					open: true,
				}}
			/>;
		}
		switch (currentStep) {
			case PortalRegistrationStep.Info:
				return <PatientMainForm onSubmit={onRegistrationSubmit} />;
			case PortalRegistrationStep.Congratulations:
				return <Congratulations onOk={setNextStep} steps={steps} />;
			case PortalRegistrationStep.Hipaa:
				return user
					&& <SignHipaa
						user={user}
						afterSubmit={(user) => {
							refreshCompanyTemplateInvites();
							setNextStep(user);
						}}
						companyTemplateInvites={hippa}
					/>;
			case PortalRegistrationStep.Photo:
				return user && <PatientPhoto renderButtons={renderButtons} user={user} afterSubmit={setNextStep} />;
			case PortalRegistrationStep.Insurance:
				return user && <InsuranceStep
					initialItems={innerPage._type === 'InsuranceRegistrationPage' ? (innerPage as InsuranceStepPage)?.items?.list : undefined}
					renderButtons={renderButtons}
					afterSubmit={setNextStep}
				/>;
			case PortalRegistrationStep.License:
				return user && <DriverLicenseStep
					initialItems={innerPage._type === 'LicenseRegistrationPage' ? (innerPage as DrivingLicenseStepPage)?.items?.list : undefined}
					renderButtons={renderButtons}
					afterSubmit={setNextStep}
				/>;
			case PortalRegistrationStep.Forms:
				return user && <RegistrationFillForms
					invites={forms}
					user={user}
					afterSubmit={(userData) => { setNextStep(userData, serverPage.redirectUrl); }}
				/>;
			default:
				return <LogoLoaderPage />;
		}
	};

	return <>
		<div className="patient-register-page">
			<ServerPageProvider
				path="registration"
				defaultTitle="Registration"
				inner={(serverPage) => {
					return <main className="container">
						{getCurrentStep(serverPage)}
					</main>;
				}}
			/>
		</div>
	</>;
};

export default connect(
	(state: ApplicationState) => ({
		user: state.login.user,
		companyTemplateInvites: state.companyTemplateInvites.items,
		userRegistrationSteps: state.userRegistrationSteps.item,
	}),
)(PatientRegistration);
